import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen therapy-bg py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 therapy-text-muted"
        >
          ← Back
        </Button>

        {/* Header */}
        <div className="therapy-bg-card border therapy-border rounded-2xl p-8 mb-6">
          <h1 className="text-4xl font-bold therapy-text mb-2">Privacy Policy</h1>
          <p className="therapy-text-muted">For i am buddy</p>
          <p className="therapy-text-muted">Effective Date: February 8, 2026</p>
          <p className="therapy-text-muted">Last Updated: February 8, 2026</p>
        </div>

        {/* Content */}
        <div className="therapy-bg-card border therapy-border rounded-2xl p-8 space-y-8 therapy-text">
          {/* Important Notice */}
          <div className="bg-amber-500/10 border-2 border-amber-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              <span>⚠️</span>
              <span>Important: We Are NOT a HIPAA-Covered Entity</span>
            </h2>
            <p className="mb-3">
              <strong>i am</strong> is a peer support and educational tool, <strong>not a healthcare provider</strong>. 
              We are not covered by HIPAA because:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>We do not provide medical treatment, diagnosis, or therapy</li>
              <li>We are not healthcare providers, health plans, or healthcare clearinghouses</li>
              <li>We do not bill insurance or handle protected health information (PHI) for covered entities</li>
            </ul>
            <p className="mt-3">
              <strong>However</strong>, we treat your data with the same level of security and care as if we were 
              HIPAA-covered, because your privacy matters to us.
            </p>
          </div>

          {/* TL;DR */}
          <div className="bg-[var(--therapy-primary)]/10 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-4">Summary (TL;DR)</h2>
            <ul className="space-y-2">
              <li>✅ We collect: Account info + your recovery journey data</li>
              <li>✅ We protect it with: Encryption, secure servers, access controls</li>
              <li>✅ We use it to: Provide personalized support and improve the app</li>
              <li>❌ We DON'T: Sell data, share with advertisers, or use for marketing</li>
              <li>✅ Your rights: Access, correct, delete, export your data anytime</li>
              <li>✅ Security: Bank-level encryption, enterprise infrastructure</li>
              <li>⚠️ We're NOT HIPAA-covered: We're your buddy in peer support, not medical treatment</li>
              <li>👤 Age: 18+ only</li>
              <li>📞 Questions: privacy@iam-buddy.app</li>
            </ul>
            <p className="mt-4 font-semibold">You own your recovery journey. You own your data. We're your buddy along the way.</p>
          </div>

          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">1. What Data We Collect and Why</h2>
            
            <h3 className="text-xl font-semibold mb-2 mt-4">Account Information</h3>
            <p className="mb-2"><strong>What we collect:</strong></p>
            <ul className="list-disc pl-6 mb-3">
              <li>Email address</li>
              <li>Password (encrypted, we never see it)</li>
              <li>Name (optional, can be a pseudonym)</li>
            </ul>
            <p className="mb-2"><strong>Why we collect it:</strong></p>
            <ul className="list-disc pl-6">
              <li>To create and secure your account</li>
              <li>To send important updates (optional)</li>
              <li>To help you recover your account if needed</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2 mt-4">Recovery Journey Data</h3>
            <p className="mb-2"><strong>What we collect:</strong></p>
            <ul className="list-disc pl-6 mb-3">
              <li>Your responses to the onboarding assessment (struggles, values, intentions)</li>
              <li>Daily check-in responses (morning intentions, midday reflections, evening gratitude)</li>
              <li>Module progress (which lessons you've completed)</li>
              <li>SOS intervention usage (what coping strategies you used and whether they helped)</li>
              <li>Progress milestones (days of engagement, skills practiced)</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">2. How We Protect Your Data</h2>
            
            <h3 className="text-xl font-semibold mb-2">Encryption</h3>
            <ul className="list-disc pl-6 mb-3">
              <li><strong>In transit</strong>: All data sent between your device and our servers is encrypted using TLS 1.2+ (the same technology banks use)</li>
              <li><strong>At rest</strong>: All data stored in our database is encrypted using AES-256 encryption</li>
              <li><strong>Passwords</strong>: Hashed using bcrypt (industry-standard, one-way encryption)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2 mt-4">Access Controls</h3>
            <ul className="list-disc pl-6">
              <li>Only you can access your data</li>
              <li>Our team cannot see your journal entries, reflections, or personal content</li>
              <li>Multi-factor authentication for our admin systems</li>
              <li>Regular security audits</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">3. Your Privacy Rights</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2">Right to Access</h3>
                <p>You can view all your data at any time in Settings → My Data</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Right to Correct</h3>
                <p>You can update your information in Settings → Profile</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Right to Delete (Right to be Forgotten)</h3>
                <p>You can delete your account and all data in Settings → Delete Account</p>
                <p className="mt-2">Process:</p>
                <ol className="list-decimal pl-6 mt-2">
                  <li>Soft delete (30 days): Account deactivated, data preserved</li>
                  <li>Hard delete (after 30 days): All data permanently deleted</li>
                  <li>Cannot be undone after hard delete</li>
                </ol>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Right to Export (Data Portability)</h3>
                <p>You can download all your data in JSON format. Email: privacy@iam-buddy.app</p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">4. Third-Party Services</h2>
            
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold">MongoDB Atlas (Database)</h3>
                <p>Enterprise-grade encryption, SOC 2 Type II certified</p>
              </div>

              <div>
                <h3 className="font-semibold">Google OAuth (Optional)</h3>
                <p>Only if you choose to sign in with Google</p>
              </div>
            </div>
            
            <p className="mt-4 font-semibold">We carefully vet all third-party services for security and privacy.</p>
          </section>

          {/* Contact */}
          <section className="bg-[var(--therapy-primary)]/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
            <div className="space-y-2">
              <p>📧 <strong>Privacy questions</strong>: privacy@iam-buddy.app</p>
              <p>📧 <strong>Security concerns</strong>: security@iam-buddy.app</p>
              <p>📧 <strong>General support</strong>: support@iam-buddy.app</p>
              <p className="mt-4">⏱️ Response time: Within 7 business days</p>
            </div>
          </section>

          {/* Full Policy Link */}
          <div className="text-center pt-6 border-t therapy-border">
            <p className="therapy-text-muted mb-4">
              This is a summary. For the complete Privacy Policy with all details, legal language, 
              and international compliance information:
            </p>
            <Button
              onClick={() => window.open('/PRIVACY_POLICY.md', '_blank')}
              className="button-primary"
            >
              Read Full Privacy Policy
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
